import { useEffect } from 'react';
import {
  mapOgRoomToNodeV2,
  type NodeRoomV2,
  useGetRoomsQuery,
  useGetRoomsForUserQuery,
} from '../../services';
import { getLogger } from '../../libs/logger';
import { getIsGraphqlRoomQueriesEnabled } from '../../utils/isGraphqlRoomQueriesEnabled';

export { type NodeRoomV2 };

const logger = getLogger('useRooms');

type UseRoomsProps = {
  userId: string;
  institutionId: number | null;
};

type UseRoomsReturnType = {
  rooms: NodeRoomV2[] | undefined;
  isLoading: boolean;
};

/**
 * Get the rooms of a provider.
 */
export const useRooms = ({
  userId,
  institutionId,
}: UseRoomsProps): UseRoomsReturnType => {
  const isGraphqlRoomQueriesEnabled = getIsGraphqlRoomQueriesEnabled();

  const {
    currentData: ogRooms,
    isLoading: loadingRtkQuery,
    error: errorRtkQuery,
  } = useGetRoomsQuery(
    { userId, institutionId },
    { skip: isGraphqlRoomQueriesEnabled },
  );

  const {
    currentData: gqlData,
    isLoading: loadingGraphqlQuery,
    error: errorGraphqlQuery,
  } = useGetRoomsForUserQuery(undefined, {
    skip: !isGraphqlRoomQueriesEnabled,
  });

  const { rooms, error, isLoading } = isGraphqlRoomQueriesEnabled
    ? {
        rooms: gqlData?.Room?.getRoomsForUser?.edges || [],
        error: errorGraphqlQuery,
        isLoading: loadingGraphqlQuery,
      }
    : {
        rooms: ogRooms?.map(mapOgRoomToNodeV2) || [],
        error: errorRtkQuery,
        isLoading: loadingRtkQuery,
      };

  useEffect(() => {
    if (error) {
      logger.error('Failed to get rooms');
    }
  }, [error]);

  return {
    rooms,
    isLoading,
  };
};
